import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, apiRequest } from "../api/client";
import OtpInput from "../components/OtpInput";
import { useCountdown } from "../hooks/useCountdown";
import AuthCard, { AUTH_INPUT } from "../components/common/AuthCard";
import { Button } from "@/components/ui/button";

/**
 * One-time setup flow. Per SPEC §13 + §19.10:
 *  - The setup token is a single-use shared secret; the backend
 *    returns 403 forever after the first successful setup
 *  - The flow is two-step: setup-start (email + token) → verify-otp
 *
 * Setup endpoints require a header `X-Setup-Token`. The token is
 * NOT stored in localStorage — the user pastes it during the flow.
 */

type Stage = "details" | "otp";

const OTP_TTL_MS = 5 * 60 * 1_000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SetupPage() {
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("details");
  const [setupToken, setSetupToken] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpDeadline, setOtpDeadline] = useState<number | null>(null);
  const otpSecondsLeft = useCountdown(otpDeadline);

  const submitDetails = async (e: FormEvent) => {
    e.preventDefault();
    if (!setupToken.trim()) {
      setError("Le token de setup est requis.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError("Entre une adresse e-mail valide.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await apiRequest("/api/auth/setup", {
        method: "POST",
        body: { email },
        headers: { "X-Setup-Token": setupToken },
        anonymous: true,
      });
      setStage("otp");
      setOtpDeadline(Date.now() + OTP_TTL_MS);
    } catch (err) {
      setError(humanSetupError(err));
    } finally {
      setPending(false);
    }
  };

  const verify = async (fullCode: string) => {
    setPending(true);
    setError(null);
    try {
      await apiRequest("/api/auth/setup/verify", {
        method: "POST",
        body: { email, code: fullCode },
        headers: { "X-Setup-Token": setupToken },
        anonymous: true,
      });
      navigate("/login", { replace: true });
    } catch (err) {
      setCode("");
      setError(humanSetupError(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthCard
      title="Configuration initiale"
      subtitle="Définis l'adresse e-mail principale du bot."
    >
      {stage === "details" && (
        <form onSubmit={submitDetails} className="flex flex-col gap-3">
          <label htmlFor="setup-token" className="text-muted-foreground text-sm">
            Setup token
          </label>
          <input
            id="setup-token"
            type="password"
            autoComplete="off"
            required
            disabled={pending}
            value={setupToken}
            onChange={(e) => setSetupToken(e.target.value)}
            className={AUTH_INPUT}
          />
          <label htmlFor="setup-email" className="text-muted-foreground text-sm">
            Email
          </label>
          <input
            id="setup-email"
            type="email"
            autoComplete="email"
            required
            disabled={pending}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={AUTH_INPUT}
          />
          {error && (
            <p className="text-loss text-sm" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? "Envoi…" : "Recevoir le code de vérification"}
          </Button>
        </form>
      )}

      {stage === "otp" && (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">
            We sent a 6-digit code to <strong className="text-foreground">{email}</strong>.
          </p>
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(full) => void verify(full)}
            disabled={pending}
            autoFocus
          />
          <p className="text-muted-foreground text-sm">
            {otpSecondsLeft > 0
              ? `Code expires in ${formatMmSs(otpSecondsLeft)}`
              : "Code expiré — reviens en arrière et demandes-en un nouveau"}
          </p>
          {error && (
            <p className="text-loss text-sm" role="alert">
              {error}
            </p>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setStage("details");
              setCode("");
              setError(null);
            }}
          >
            Back
          </Button>
        </div>
      )}
    </AuthCard>
  );
}

function humanSetupError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Token de setup ou OTP invalide.";
    if (err.status === 403) return "Le setup a déjà été effectué.";
    if (err.status === 429) return "Trop de tentatives. Réessaie plus tard.";
  }
  return "Setup échoué. Réessaie.";
}

function formatMmSs(secs: number): string {
  const m = Math.floor(secs / 60).toString();
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}
